"use client";

import type { CSSProperties, ReactNode } from "react";

/**
 * Shared presentation primitives for the trip app. Every trip page used to
 * hand-roll these (the card wrapper, BOOKED pill, loading/empty states, the
 * full-page spinner, the offline banner) with slightly drifting copies —
 * they live here once now. Styling stays inline-style based to match the
 * trip app's existing approach; colors come from the globals.css tokens.
 */

/* ── Style fragments reused by pages that need the raw objects ──────────── */

/** Outer card — the bordered block every trip page lists content in. */
export const cardStyle: CSSProperties = {
  marginBottom: 16,
  background: "var(--bg)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "16px 18px",
};

/** Small mono uppercase label (section eyebrows, nav links). */
export const monoLabelStyle: CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--mono)",
  color: "var(--text3)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

/* ── Components ─────────────────────────────────────────────────────────── */

export function Card({ style, children }: { style?: CSSProperties; children: ReactNode }) {
  return <div style={{ ...cardStyle, ...style }}>{children}</div>;
}

/** Inner white sub-card used for booking details inside a Card. */
export function InnerCard({ style, children }: { style?: CSSProperties; children: ReactNode }) {
  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: "12px 14px", ...style }}>
      {children}
    </div>
  );
}

/** Green status pill — "BOOKED", "Latest", etc. */
export function Badge({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        flexShrink: 0,
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.06em",
        fontFamily: "var(--mono)",
        background: "var(--green-bg)",
        color: "var(--green)",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

/** Centered in-tab placeholder while the trip data loads. */
export function TabLoading() {
  return <div style={{ textAlign: "center", padding: "48px 0", color: "var(--text3)" }}>Loading...</div>;
}

/** Centered in-tab message for tabs with nothing to show yet. */
export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text3)", lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

/** Full-page centered spinner with a mono status line. The spin keyframes
 * live in globals.css. */
export function FullPageSpinner({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: 16 }}>
      <div style={{ width: 32, height: 32, border: "3px solid var(--border)", borderTopColor: "var(--text)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <div style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--text3)", letterSpacing: "0.02em" }}>
        {label}
      </div>
    </div>
  );
}

/** The full-width OFFLINE banner shown at the top of trip screens. */
export function OfflineBanner() {
  return (
    <div
      style={{
        background: "var(--warn)",
        color: "#fff",
        textAlign: "center",
        padding: "4px 0",
        fontSize: 11,
        fontFamily: "var(--mono)",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        marginBottom: 16,
        borderRadius: 4,
      }}
    >
      Offline
    </div>
  );
}
